import React, { useCallback, useMemo, useRef } from 'react';

import { defineMessage, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { type AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import { token } from '@atlaskit/tokens';
import { ContentRenderer } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/ContentRenderer';
import { MessageRenderer } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/MessageRenderer';
import { appendContent } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { shouldNotRenderLLMContentForAction } from '@atlassian/conversation-assistant-message-actions/controllers/message-actions';
import {
	type ChatMessage,
	isHumanMessage,
	isStreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { type StoreError } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import type {
	StoreAssistant,
	StoreChatItem,
	StoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { Paper, PaperErrorMessage } from '@atlassian/conversation-assistant-ui-components/ui/Paper';

import { adfSmartlinksPlugin } from '../../common/utils/adf-smartlinks-plugin';
import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { createStoreAssistantWithContent } from '../../controllers/chat-store/createStoreAssistantWithContent';
import { createStoreHumanWithContent } from '../../controllers/chat-store/createStoreHumanWithContent';
import { normalizeFinalizedMessagePresentation } from '../../controllers/chat-store/normalizeFinalizedMessagePresentation';

import { buildRespondedAgentIdByHumanMessageId } from './build-responded-agent-id-by-human-message-id';
import { type ChatHistoryMessageRenderers, ChatTranscript } from './ChatTranscript';
import { injectRedactScribbles } from './store-assistant/injectRedactScribbles';
import { RedactScribbleExtension } from './store-assistant/RedactScribbleExtension';
import { StoreErrorView } from './store-error';
import { StoreFollowUpView } from './store-follow-up';
import { StoreGuardBlockView } from './store-guard-block';
import { ContentContainer } from './store-human/content-container';
import { extractText } from './store-human/extract-text';
import { isMessageEmpty } from './store-human/isMessageEmpty';
import { RedactedPromptContent } from './store-human/redacted-prompt/RedactedPromptContent';
import { RedactionEnforcementMessage } from './store-human/redacted-prompt/RedactionEnforcementMessage';
import { StoreHumanView } from './store-human/StoreHumanView';
import { useMessageGroups } from './use-message-groups';

const READ_ONLY_CHAT_HISTORY_ID = 'read-only-chat-history';
const CONFLUENCE_SUGGESTION_KEY = 'Confluence-Suggestion-Plugin';

const actionUnavailableMessage = defineMessage({
	id: 'rovo-chat.read-only-history.action-unavailable.ai-non-final',
	defaultMessage: 'Action unavailable in this read-only view',
	description:
		'Text shown in a read-only chat response where an interactive action originally appeared. It explains that the action cannot be performed from the current view.',
});

const styles = cssMap({
	actionPlaceholder: {
		display: 'inline-flex',
		paddingBlockStart: token('space.075'),
		paddingInlineEnd: token('space.150'),
		paddingBlockEnd: token('space.075'),
		paddingInlineStart: token('space.150'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	fileList: {
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'flex-end',
		gap: token('space.050'),
		listStyle: 'none',
		margin: 0,
		padding: 0,
		paddingBlockStart: token('space.100'),
	},
	fileName: {
		wordBreak: 'break-word',
	},
});

type ConfluenceSuggestionAction = {
	key: typeof CONFLUENCE_SUGGESTION_KEY;
	data?: {
		content?: string;
	};
};

type ReadOnlyStoreHumanViewProps = {
	storeHuman: StoreHuman;
	respondedAgentId?: string;
};

const ReadOnlyStoreHumanView = ({
	storeHuman,
	respondedAgentId,
}: ReadOnlyStoreHumanViewProps): React.JSX.Element | null => {
	const {
		content: { content, file_uploads },
		redactedPrompt,
	} = storeHuman;
	const {
		productKey,
		siteId,
		profileData: { aaid },
	} = useGlobalConfig();
	const { customHandlers } = useUIConfig();

	const copyContent = useCallback(() => {
		const plainText =
			typeof content === 'object' && content.type === 'doc'
				? extractText(content)
				: (content?.toString() ?? '');
		void navigator.clipboard?.writeText(plainText);
	}, [content]);

	if (isMessageEmpty(storeHuman)) {
		return null;
	}

	if (redactedPrompt && fg('rovo-guard-ui-redaction-and-blocking')) {
		return (
			<>
				<ContentContainer>
					<RedactedPromptContent redactedPrompt={redactedPrompt} />
				</ContentContainer>
				<RedactionEnforcementMessage />
			</>
		);
	}

	return (
		<>
			<ContentContainer onContentCopy={copyContent}>
				<ContentRenderer
					content={content}
					productKey={productKey}
					siteId={siteId}
					aaid={aaid}
					theme="inverse"
					onDeepLinkClick={customHandlers?.handleDeepLinkClick}
					respondedAgentId={respondedAgentId}
				/>
			</ContentContainer>
			{!!file_uploads?.length && fg('rovo_enable_file_upload') && (
				<Box as="ul" xcss={styles.fileList}>
					{file_uploads.map((file) => (
						<Box as="li" key={file.id} xcss={styles.fileName}>
							<Text size="small">{file.name}</Text>
						</Box>
					))}
				</Box>
			)}
		</>
	);
};

type ReadOnlyStoreAssistantViewProps = {
	storeAssistant: StoreAssistant;
};

type ReadOnlyMessageActionsProps = {
	actions: MessageAction[];
};

const ReadOnlyMessageActions = ({
	actions,
}: ReadOnlyMessageActionsProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	return actions.length ? (
		<Box paddingBlockStart="space.100">
			<Box xcss={styles.actionPlaceholder}>
				<Text color="color.text.disabled">{formatMessage(actionUnavailableMessage)}</Text>
			</Box>
		</Box>
	) : null;
};

const ReadOnlyStoreAssistantView = ({
	storeAssistant,
}: ReadOnlyStoreAssistantViewProps): React.JSX.Element | null => {
	const {
		productKey,
		siteId,
		orgId,
		profileData: { aaid },
	} = useGlobalConfig();
	const { customHandlers } = useUIConfig();
	const {
		content: { message: assistantResponse },
		markdownData,
		redactedResponse,
	} = storeAssistant;
	const { actions, content } = assistantResponse.message;

	const { filteredActions, message } = useMemo(() => {
		const confluenceSuggestion = actions?.find(
			(action) => action.key === CONFLUENCE_SUGGESTION_KEY,
		) as ConfluenceSuggestionAction | undefined;
		const filteredActions = actions?.filter((action) => action.key !== CONFLUENCE_SUGGESTION_KEY);
		const suggestionContent = confluenceSuggestion?.data?.content;

		return {
			filteredActions,
			message: suggestionContent
				? {
						...assistantResponse.message,
						content: appendContent(content, `---\n\n ${suggestionContent}`),
					}
				: assistantResponse.message,
		};
	}, [actions, assistantResponse.message, content]);

	if (redactedResponse && fg('rovo-guard-ui-redaction-and-blocking')) {
		return (
			<Stack space="space.050">
				<Paper showPadding={false} variant="neutral" pinchCorner="topLeft">
					<ContentRenderer
						content={redactedResponse}
						productKey={productKey}
						siteId={siteId}
						adfTransformer={injectRedactScribbles}
						extensionComponent={RedactScribbleExtension}
					/>
				</Paper>
				<RedactionEnforcementMessage alignInline="start" />
			</Stack>
		);
	}

	if (!content && !filteredActions?.length) {
		return null;
	}

	const shouldRenderContent = !filteredActions?.some(shouldNotRenderLLMContentForAction);

	return (
		<Paper showPadding={false} variant="neutral" pinchCorner="topLeft">
			{shouldRenderContent && (
				<MessageRenderer
					message={message}
					allowDownloadCodeBlock
					markdownData={markdownData}
					productKey={productKey}
					siteId={siteId}
					orgId={orgId}
					aaid={aaid}
					onDeepLinkClick={customHandlers?.handleDeepLinkClick}
					isExperimentEnabled={(experimentName: string, needsExposure?: boolean) =>
						FeatureGates.getExperimentValue(experimentName, 'isEnabled', false, {
							fireExperimentExposure: needsExposure ?? true,
						})
					}
					adfTransformer={adfSmartlinksPlugin}
					{...(fg('rovo_chat_history_lift') && {
						interactionMode: 'read-only' as const,
					})}
				/>
			)}
			<ReadOnlyMessageActions actions={filteredActions ?? []} />
		</Paper>
	);
};

type ReadOnlyStoreErrorViewProps = {
	error: StoreError;
};

const ReadOnlyStoreErrorView = ({ error }: ReadOnlyStoreErrorViewProps): React.JSX.Element => (
	<PaperErrorMessage message={error.content} variant="neutral" pinchCorner="bottomLeft" />
);

const normalizeReadOnlyMessages = (messages: ReadonlyArray<ChatMessage>): StoreChatItem[] => {
	const normalizedMessages: StoreChatItem[] = [];

	for (const message of messages) {
		if (isHumanMessage(message)) {
			normalizedMessages.push(createStoreHumanWithContent(message));
			continue;
		}

		if (!isStreamResponse(message)) {
			continue;
		}

		const creatorType = message.message.message.author?.creator_type as
			| AgentCreatorType
			| undefined;
		const normalizedPresentation = normalizeFinalizedMessagePresentation({
			message,
			options: {
				shouldExtractA2UI: fg('jira_ai_rendering_a2ui'),
				shouldExtractActionableFollowUps: fg('rovo_chat_actionable_followups_enabled'),
				shouldExtractDataSculptor: fg('rovo_enable_data_sculptor'),
				shouldExtractInlineLumina: fg('rovo_chat_enable_inline_lumina'),
				shouldExtractLegacyChartBoundaries: true,
				shouldPreprocessDetails: !!creatorType && isForgeAgentByCreatorType(creatorType),
			},
		});
		const storeAssistant = createStoreAssistantWithContent(normalizedPresentation.message);

		if (Object.keys(normalizedPresentation.markdownData).length > 0) {
			storeAssistant.markdownData = normalizedPresentation.markdownData;
		}
		if (normalizedPresentation.agenticUiMessages) {
			storeAssistant.agenticUiMessages = normalizedPresentation.agenticUiMessages;
		}

		storeAssistant.isHistorical = true;
		normalizedMessages.push(storeAssistant);
	}

	return normalizedMessages;
};

/**
 * This props object intentionally starts with source-adapted finalized messages
 * only. Keeping a named object boundary allows optional conversation-level
 * context to be added later without changing existing callers.
 */
export type ReadOnlyChatHistoryControllerProps = {
	messages: ReadonlyArray<ChatMessage>;
};

export const ReadOnlyChatHistoryController = ({
	messages,
}: ReadOnlyChatHistoryControllerProps): React.JSX.Element => {
	const chatHistoryContainerRef = useRef<HTMLDivElement>(null);
	const lastMessageGroupRef = useRef<HTMLDivElement>(null);
	const normalizedMessages = useMemo(() => normalizeReadOnlyMessages(messages), [messages]);
	const messageGroups = useMessageGroups(normalizedMessages);
	const respondedAgentIdByHumanMessageId = useMemo(
		() =>
			buildRespondedAgentIdByHumanMessageId(
				normalizedMessages,
				isExperimentEnabled('rovo_chat_mention_agents'),
			),
		[normalizedMessages],
	);

	const messageRenderers = useMemo<ChatHistoryMessageRenderers>(
		() => ({
			human: (storeHuman, { index }) => {
				const key = `read-only-history-message-${index}`;
				const respondedAgentId = respondedAgentIdByHumanMessageId.get(
					String(storeHuman.content.id),
				);
				if (fg('rovo_chat_history_lift')) {
					return (
						<StoreHumanView
							key={key}
							interactionMode="read-only"
							storeHuman={storeHuman}
							respondedAgentId={respondedAgentId}
						/>
					);
				}
				return (
					<ReadOnlyStoreHumanView
						key={key}
						storeHuman={storeHuman}
						respondedAgentId={respondedAgentId}
					/>
				);
			},
			assistant: (storeAssistant, { index }) => (
				<Box
					key={`read-only-history-message-${index}`}
					data-scroll-anchor="assistant-message"
					data-message-id={storeAssistant.content.message.message.id}
				>
					<ReadOnlyStoreAssistantView storeAssistant={storeAssistant} />
				</Box>
			),
			followUp: (storeFollowUp, { index }) =>
				fg('rovo_chat_history_lift') ? (
					<Box key={`read-only-history-message-${index}`} data-scroll-anchor="follow-up-message">
						<StoreFollowUpView interactionMode="read-only" followUp={storeFollowUp} />
					</Box>
				) : null,
			guardBlock: (storeGuardBlock, { index }) =>
				fg('rovo-guard-ui-redaction-and-blocking') ? (
					<Box key={`read-only-history-message-${index}`} data-scroll-anchor="guard-block-message">
						<StoreGuardBlockView
							{...(fg('rovo_chat_history_lift') && {
								interactionMode: 'read-only' as const,
							})}
							traceId={storeGuardBlock.traceId}
							blockType={storeGuardBlock.blockType}
						/>
					</Box>
				) : null,
			error: (storeError, { index }) => (
				<Box key={`read-only-history-message-${index}`} data-scroll-anchor="error-message">
					{fg('rovo_chat_history_lift') ? (
						<StoreErrorView interactionMode="read-only" error={storeError} />
					) : (
						<ReadOnlyStoreErrorView error={storeError} />
					)}
				</Box>
			),
		}),
		[respondedAgentIdByHumanMessageId],
	);

	return (
		<ChatTranscript
			isFullScreenErrorState={false}
			isPulseExperimentEnabled={false}
			shouldShowConversationHistoryWarning={false}
			conversationMessagesResponseCount={normalizedMessages.length}
			conversationHistoryWarningMessage={null}
			showSkills={null}
			showPromptLibrary={null}
			chatHistoryContainerRef={chatHistoryContainerRef}
			messagePresentation={{
				renderers: messageRenderers,
				default: {
					shouldRenderLiveAssistantAffordances: false,
				},
			}}
			messageGroupsContentProps={{
				conversationId: READ_ONLY_CHAT_HISTORY_ID,
				messageGroups,
				olderMessageGroups: [],
				isVirtualizationEnabled: false,
				isRenderStabilizationEnabled: false,
				chatHistoryContainerRef,
				isScrollPending: false,
				lastMessageGroupRef,
				scrollToBottomGeneration: 0,
			}}
		/>
	);
};
